/**
 * Sites and their geofences.
 *
 * Editing a radius or moving a pin silently changes how every future clock-in is judged,
 * which is why the warning below is on the page and every write is audited server-side.
 */

import { lazy, Suspense, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddressSearch } from '@/components/AddressSearch';
import { MobileList } from '@/components/MobileList';
import { Modal } from '@/components/Modal';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type LatLng, type Site } from '@/lib/api';
import { useTableState, type SortOption } from '@/lib/table';
import { Combobox } from '@/components/Combobox';
import { ExportSheetButton, ImportSheetDialog } from '@/features/sheets/ExcelSheet';

/*
 * MapLibre is ~800 kB — nearly as much as the rest of the portal put together, and it is
 * needed on exactly one form that most sessions never open. Loading it on demand keeps
 * that weight off the dashboard, which is the screen people actually leave open all day.
 */
const SiteMap = lazy(() => import('@/components/SiteMap').then((m) => ({ default: m.SiteMap })));

const SITE_SORTS: SortOption<Site>[] = [
  { key: 'name', labelKey: 'employees.name', value: (s) => s.name },
  { key: 'client', labelKey: 'sites.client', value: (s) => s.client?.name },
  { key: 'city', labelKey: 'sites.address', value: (s) => s.city },
  {
    key: 'radius',
    labelKey: 'sites.area',
    value: (s) => s.geofenceRadiusM,
    defaultDirection: 'desc',
  },
];

/** The client picker's "type a new one" choice. */
const NEW_CLIENT = '__new';

const EMPTY = {
  name: '',
  /** '' = no client, NEW_CLIENT = create one from `newClientName`, else a client id. */
  clientId: '',
  newClientName: '',
  addressLine1: '',
  city: '',
  province: '',
  postcode: '',
  lat: '',
  lng: '',
  geofenceRadiusM: '150',
  accessNotes: '',
  // Empty string means "no override, follow the general setting". Distinct from '0',
  // which for the tolerance is a real value meaning no grace at all.
  edgeToleranceM: '0',
  accuracyTrustThresholdM: '',
  accuracyRejectThresholdM: '',
  /** '' = follow the global setting, 'yes' / 'no' = pin it for this site. */
  allowOutside: '',
  /*
   * `undefined` and `null` mean different things all the way down to the SQL: undefined
   * leaves a stored boundary alone, null deletes it. Losing that distinction here would
   * mean every edit of a site's name silently wiped its boundary — and changed how every
   * future clock-in on it is judged.
   */
  geofencePolygon: undefined as LatLng[] | null | undefined,
};

export function SitesPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Site | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });
  const clients = useQuery({ queryKey: ['clients'], queryFn: api.clients });

  /** The client id to save: a new client is created first, by name. */
  const resolveClientId = async (): Promise<string | null> => {
    if (form.clientId === NEW_CLIENT) {
      const name = form.newClientName.trim();
      if (!name) return null;
      const { client } = await api.createClient({ name });
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      return client.id;
    }
    return form.clientId || null;
  };

  // Address and access notes are searchable too: "cancello laterale" is how somebody
  // in the office actually remembers a site they visited once.
  const table = useTableState<Site>({
    rows: sites.data?.sites ?? [],
    search: (s) => [s.name, s.client?.name, s.addressLine1, s.city, s.postcode, s.accessNotes],
    sorts: SITE_SORTS,
    defaultSortKey: 'name',
    storageKey: 'sites',
  });

  const reset = async () => {
    setCreating(false);
    setEditing(null);
    setForm(EMPTY);
    await queryClient.invalidateQueries({ queryKey: ['sites'] });
  };

  const payload = () => ({
    name: form.name.trim(),
    addressLine1: form.addressLine1.trim() || undefined,
    city: form.city.trim() || undefined,
    province: form.province.trim() || undefined,
    postcode: form.postcode.trim() || undefined,
    lat: form.lat ? Number(form.lat) : undefined,
    lng: form.lng ? Number(form.lng) : undefined,
    geofenceRadiusM: Number(form.geofenceRadiusM),
    accessNotes: form.accessNotes.trim() || undefined,
    geofencePolygon: form.geofencePolygon,
    edgeToleranceM: Number(form.edgeToleranceM) || 0,
    // null, not undefined: an emptied box means "remove the override", and the server
    // tells those two apart on purpose.
    accuracyTrustThresholdM: form.accuracyTrustThresholdM
      ? Number(form.accuracyTrustThresholdM)
      : null,
    accuracyRejectThresholdM: form.accuracyRejectThresholdM
      ? Number(form.accuracyRejectThresholdM)
      : null,
    allowOutside: form.allowOutside === '' ? null : form.allowOutside === 'yes',
  });

  const create = useMutation({
    mutationFn: async () => {
      const clientId = await resolveClientId();
      return api.createSite({ ...payload(), clientId: clientId ?? undefined });
    },
    onSuccess: reset,
  });
  const update = useMutation({
    // null takes the site off its client; the server tells that apart from "absent".
    mutationFn: async (id: string) => api.updateSite(id, { ...payload(), clientId: await resolveClientId() }),
    onSuccess: reset,
  });

  const startEdit = (site: Site) => {
    setCreating(false);
    setEditing(site.id);
    setForm({
      name: site.name,
      clientId: site.client?.id ?? '',
      newClientName: '',
      addressLine1: site.addressLine1 ?? '',
      city: site.city ?? '',
      province: '',
      postcode: site.postcode ?? '',
      lat: site.lat === null ? '' : String(site.lat),
      lng: site.lng === null ? '' : String(site.lng),
      geofenceRadiusM: String(site.geofenceRadiusM),
      accessNotes: site.accessNotes ?? '',
      // Seeded with what is stored, so the map shows the real boundary and an edit
      // that never touches it sends back exactly what was already there.
      geofencePolygon: site.geofencePolygon,
      edgeToleranceM: String(site.edgeToleranceM),
      accuracyTrustThresholdM:
        site.accuracyTrustThresholdM === null ? '' : String(site.accuracyTrustThresholdM),
      accuracyRejectThresholdM:
        site.accuracyRejectThresholdM === null ? '' : String(site.accuracyRejectThresholdM),
      allowOutside: site.allowOutside === null ? '' : site.allowOutside ? 'yes' : 'no',
    });
  };

  /*
   * The pin, from whatever is in the two coordinate boxes.
   *
   * They are strings because they are text inputs, and half-typed ones are normal — "45."
   * is what a latitude looks like mid-keystroke. Number('45.') is 45, but Number('') is
   * 0, which would drop the pin in the Gulf of Guinea, so an empty box means no pin.
   */
  const pin =
    form.lat.trim() &&
    form.lng.trim() &&
    Number.isFinite(Number(form.lat)) &&
    Number.isFinite(Number(form.lng))
      ? { lat: Number(form.lat), lng: Number(form.lng) }
      : null;

  /**
   * Move the pin, and fill in whatever address is at the new spot.
   *
   * The coordinates are applied immediately — the map must not lag behind the drag —
   * and the reverse lookup catches up a moment later. It only ever fills fields that
   * are EMPTY: someone who dropped a pin after typing the address by hand meant the
   * address they typed, and having it rewritten from under them would be worse than
   * leaving it alone.
   */
  const movePin = (next: LatLng) => {
    setForm((current) => ({
      ...current,
      // Six decimals is about 10 cm. More is noise from a drag.
      lat: next.lat.toFixed(6),
      lng: next.lng.toFixed(6),
    }));

    void api
      .reverseGeocode(next)
      .then(({ suggestion }) => {
        if (!suggestion) return;
        setForm((current) => ({
          ...current,
          addressLine1: current.addressLine1 || (suggestion.addressLine1 ?? suggestion.label),
          city: current.city || (suggestion.city ?? ''),
          province: current.province || (suggestion.province ?? ''),
          postcode: current.postcode || (suggestion.postcode ?? ''),
        }));
      })
      // Nothing to report: the pin moved, which is what was asked for. The address
      // fields simply stay as they were.
      .catch(() => {});
  };

  const remove = useMutation({
    mutationFn: (site: Site) => api.deleteSite(site.id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      await queryClient.invalidateQueries({ queryKey: ['recycle-bin'] });
    },
  });

  const error = create.error ?? update.error;
  const busy = create.isPending || update.isPending;
  const formOpen = creating || editing !== null;

  return (
    <>
      <PageHeader
        title={t('sites.title')}
        subtitle={t('sites.subtitle')}
        actions={
          <>
            <ExportSheetButton kind="sites" />
            <button type="button" className="btn-secondary" onClick={() => setImporting(true)}>
              {t('sheet.import')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY);
                setCreating((v) => !v);
              }}
            >
              {creating ? t('common.cancel') : t('sites.newSite')}
            </button>
          </>
        }
      />

      {/*
        Deletion asks, and names the site.
        It is reversible for thirty days, so this is a speed bump rather than a wall —
        but a site is what every clock-in on it is judged against, and removing one
        without a pause is the kind of click people make by accident once.
      */}
      <ImportSheetDialog kind="sites" open={importing} onClose={() => setImporting(false)} />

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        size="sm"
        title={t('sites.deleteTitle')}
        description={deleting ? t('sites.deleteConfirm', { name: deleting.name }) : ''}
        footer={
          <>
            <button
              type="button"
              className="btn-danger"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {remove.isPending ? t('common.saving') : t('common.delete')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {remove.error ? <ErrorNotice error={remove.error} /> : null}
      </Modal>

      <Modal
        open={formOpen}
        onClose={() => void reset()}
        size="lg"
        title={editing ? t('sites.edit') : t('sites.create')}
        description={t('sites.geofenceWarning')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={form.name.trim().length < 2 || busy}
              onClick={() => (editing ? update.mutate(editing) : create.mutate())}
            >
              {busy ? t('common.saving') : editing ? t('common.save') : t('sites.create')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => void reset()}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="col-span-3">
            <Field label={t('employees.name')}>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('sites.namePlaceholder')}
              />
            </Field>
          </div>
          <div className="col-span-3">
            <Field label={t('sites.client')} hint={t('sites.clientHint')}>
              <div className="flex flex-col sm:flex-row gap-2">
                <Combobox
                  className="sm:max-w-xs"
                  value={form.clientId}
                  onChange={(clientId) => setForm({ ...form, clientId })}
                  options={[
                    { value: '', label: t('sites.noClient') },
                    ...(clients.data?.clients ?? []).map((c) => ({ value: c.id, label: c.name })),
                    { value: NEW_CLIENT, label: t('sites.newClient') },
                  ]}
                />
                {form.clientId === NEW_CLIENT ? (
                  <input
                    className="input flex-1"
                    value={form.newClientName}
                    onChange={(e) => setForm({ ...form, newClientName: e.target.value })}
                    placeholder={t('sites.newClientPlaceholder')}
                    autoFocus
                  />
                ) : null}
              </div>
            </Field>
          </div>
          <div className="col-span-3">
            <Field label={t('sites.addressSearch')} hint={t('sites.addressSearchHint')}>
              <AddressSearch
                value={form.addressLine1}
                onChange={(next) => setForm({ ...form, addressLine1: next })}
                near={pin ?? undefined}
                placeholder={t('sites.addressPlaceholder')}
                onPick={(s) =>
                  setForm((current) => ({
                    ...current,
                    // Only overwrite what the result actually carries: a town-level
                    // match should not blank a postcode somebody typed by hand.
                    addressLine1: s.addressLine1 ?? s.label,
                    city: s.city ?? current.city,
                    province: s.province ?? current.province,
                    postcode: s.postcode ?? current.postcode,
                    lat: s.lat === null ? current.lat : String(s.lat),
                    lng: s.lng === null ? current.lng : String(s.lng),
                  }))
                }
              />
            </Field>
          </div>
          <Field label={t('common.city')}>
            <input
              className="input"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </Field>
          <Field label={t('common.postcode')}>
            <input
              className="input"
              value={form.postcode}
              onChange={(e) => setForm({ ...form, postcode: e.target.value })}
            />
          </Field>
          <Field label={t('common.latitude')}>
            <input
              className="input tabular"
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              placeholder="45.4642"
            />
          </Field>
          <Field label={t('common.longitude')}>
            <input
              className="input tabular"
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              placeholder="9.1900"
            />
          </Field>
          <Field label={t('sites.radiusLabel')} hint={t('sites.radiusHint')}>
            <input
              className="input tabular"
              type="number"
              min={25}
              max={5000}
              value={form.geofenceRadiusM}
              onChange={(e) => setForm({ ...form, geofenceRadiusM: e.target.value })}
            />
          </Field>
          <div className="col-span-3">
            {/* After the coordinates, not before: the map is the CHECK on them. Seeing
                  the pin land on the right building is the thing that catches a typo in
                  a latitude, and a boundary is only worth drawing once the pin is right. */}
            <Field label={t('sites.mapLabel')} group>
              <Suspense
                fallback={
                  <div className="h-72 rounded border border-border grid place-items-center text-sm text-muted">
                    {t('common.loading')}
                  </div>
                }
              >
                <SiteMap
                  lat={pin?.lat ?? null}
                  lng={pin?.lng ?? null}
                  radiusM={Number(form.geofenceRadiusM) || 150}
                  polygon={form.geofencePolygon ?? null}
                  onMovePin={movePin}
                  onChangePolygon={(next) =>
                    setForm((current) => ({
                      ...current,
                      geofencePolygon: next,
                    }))
                  }
                />
              </Suspense>
            </Field>
          </div>

          {/*
            Tolerances, after the map.
            They belong to a site rather than to the system because the system's numbers
            are right for a building and wrong for an airport — and somebody on the far
            apron being flagged every morning is how an exception queue stops being
            read. Blank means "follow the general setting", so an ordinary site never
            has to think about any of it.
          */}
          <div className="col-span-3 pt-3 border-t border-border">
            <div className="label-text mb-1">{t('sites.tolerances')}</div>
            <p className="text-xs text-muted mb-3 leading-relaxed">{t('sites.tolerancesHint')}</p>

            {Number(form.geofenceRadiusM) > 500 && !form.geofencePolygon ? (
              <p className="text-xs text-warning mb-3 leading-relaxed">
                {t('sites.largeSiteWarning')}
              </p>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label={t('sites.edgeTolerance')} hint={t('sites.edgeToleranceHint')}>
                <input
                  className="input tabular"
                  type="number"
                  min={0}
                  max={1000}
                  value={form.edgeToleranceM}
                  onChange={(e) => setForm({ ...form, edgeToleranceM: e.target.value })}
                />
              </Field>

              <Field label={t('sites.trustOverride')}>
                <input
                  className="input tabular"
                  type="number"
                  min={5}
                  max={1000}
                  placeholder={t('sites.overrideEmpty')}
                  value={form.accuracyTrustThresholdM}
                  onChange={(e) => setForm({ ...form, accuracyTrustThresholdM: e.target.value })}
                />
              </Field>

              <Field label={t('sites.rejectOverride')}>
                <input
                  className="input tabular"
                  type="number"
                  min={10}
                  max={2000}
                  placeholder={t('sites.overrideEmpty')}
                  value={form.accuracyRejectThresholdM}
                  onChange={(e) => setForm({ ...form, accuracyRejectThresholdM: e.target.value })}
                />
              </Field>

              <div className="col-span-3">
                <Field label={t('sites.allowOutsideOverride')}>
                  <select
                    className="input"
                    value={form.allowOutside}
                    onChange={(e) => setForm({ ...form, allowOutside: e.target.value })}
                  >
                    <option value="">{t('sites.useGlobal')}</option>
                    <option value="yes">{t('sites.forceAllow')}</option>
                    <option value="no">{t('sites.forceDeny')}</option>
                  </select>
                </Field>
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <Field label={t('common.accessNotes')} hint={t('sites.accessNotesHint')}>
              <textarea
                className="input h-20 py-2 resize-none"
                value={form.accessNotes}
                onChange={(e) => setForm({ ...form, accessNotes: e.target.value })}
              />
            </Field>
          </div>
        </div>

        {error ? (
          <p className="text-sm text-danger mt-3">
            {error instanceof ApiError ? error.message : t('common.saveError')}
          </p>
        ) : null}
      </Modal>

      {sites.isLoading ? (
        <Spinner />
      ) : sites.error ? (
        <ErrorNotice error={sites.error} />
      ) : (
        <>
          <DataToolbar
            state={table}
            searchPlaceholder={t('sites.searchPlaceholder')}
            countOne="sites.countOne"
            countMany="sites.countMany"
          />

          <Card padded={false}>
            <div className="lg:hidden p-2">
              <MobileList<Site>
                rows={table.rows}
                keyOf={(s) => s.id}
                title={(s) => s.name}
                subtitle={(s) => `${s.addressLine1}${s.city ? `, ${s.city}` : ''}`}
                badges={(s) =>
                  s.lat === null || s.lng === null ? (
                    <Badge tone="warning">{t('sites.missing')}</Badge>
                  ) : null
                }
                fields={[
                  { label: t('sites.client'), value: (s) => s.client?.name ?? null },
                  {
                    label: t('sites.coordinates'),
                    value: (s) =>
                      s.lat !== null && s.lng !== null
                        ? `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`
                        : null,
                  },
                  {
                    label: t('sites.area'),
                    value: (s) => (s.geofenceRadiusM === null ? null : `${s.geofenceRadiusM} m`),
                  },
                ]}
                action={(s) => (
                  <button type="button" className="btn-primary" onClick={() => startEdit(s)}>
                    {t('common.edit')}
                  </button>
                )}
              />
            </div>

            <div className="table-scroll max-lg:hidden">
              <table className="table-wide">
                <thead>
                  <tr>
                    <SortableTh state={table} sortKey="name">
                      {t('employees.name')}
                    </SortableTh>
                    <SortableTh state={table} sortKey="client">
                      {t('sites.client')}
                    </SortableTh>
                    <SortableTh state={table} sortKey="city">
                      {t('sites.address')}
                    </SortableTh>
                    <th className="th">{t('sites.coordinates')}</th>
                    <SortableTh state={table} sortKey="radius">
                      {t('sites.area')}
                    </SortableTh>
                    <th className="th" />
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((s) => (
                    <tr key={s.id} className="hover:bg-surface-alt">
                      <td className="td font-semibold">{s.name}</td>
                      <td className="td text-muted">{s.client?.name ?? '—'}</td>
                      <td className="td text-muted">
                        {s.addressLine1}
                        {s.city ? `, ${s.city}` : ''}
                      </td>
                      <td className="td tabular text-muted text-xs whitespace-nowrap">
                        {s.lat !== null && s.lng !== null ? (
                          `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`
                        ) : (
                          <Badge tone="warning">{t('sites.missing')}</Badge>
                        )}
                      </td>
                      <td className="td tabular whitespace-nowrap">{s.geofenceRadiusM} m</td>
                      <td className="td text-end">
                        <button
                          type="button"
                          className="td-action text-primary hover:underline"
                          onClick={() => startEdit(s)}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          className="td-action text-danger hover:underline ms-3"
                          disabled={remove.isPending}
                          onClick={() => setDeleting(s)}
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
          </Card>
        </>
      )}
    </>
  );
}
